import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Images,
    useModal,
    goalDataTypeEnums,
    sessionStatusEnums,
    trialStatusEnums,
    FindLoad,
    renderTargetStatuses,
    targetStatusEnums,
    FindSuccessItem,
} from 'utils';
import { ManageActions, NotError, SimpleTooltip, StatusCard } from 'components';
import { httpRequestsOnLoadActions, httpRequestsOnSuccessActions } from 'store';
import {
    checkPrevNext,
    formatTime,
    Frequency,
    GridDuration,
    GridRate,
    GridWholeInterval, Irt, GridIrt,
    Latency,
    Percent,
    Rating,
    renderCardColor,
    renderColorValue,
    Scale,
} from './constants';
import { SAVE_SESSION_TARGET_OPTION_SUCCESS } from '../../../../../store/clinical/session/session.types';
import { authService } from '../../../../../store/clinical/session/session.service';
import { RateHistoryLog, DurationHistoryLog, TrialHistoryLog, DELETE_TRIAL_LOG_ACTION_TYPE } from '../trialHistory';
import { findInProgressTrial, trialCapMessage, useTrialCap } from './constants';
import moment from 'moment/moment';
import { toast } from 'react-toastify';

const ACTION_TYPE_SUCCESS = 'SAVE_SESSION_TARGET_OPTION_SUCCESS';

export const SessionTargetGridCard = ({ item, goal }) => {
    const sessionById = useSelector((state) => state.session.sessionById);
    const dispatch = useDispatch();
    // IN_PROGRESS trials are persisted but not yet user-finalised, so UI navigation
    // and the "current" pointer must operate on the COMPLETED set only — otherwise
    // a half-finished timer would show as the latest completed data point.
    const completedTrials =
        item?.tSummary?.trials?.filter((t) => t?.status !== trialStatusEnums.IN_PROGRESS) ||
        item?.tSummary?.trials ||
        [];
    const [index, setIndex] = useState(completedTrials.length + 1 || 1);
    const current = completedTrials[index - 1];
    const { maxTrials, creationBlocked, completedAtCap } = useTrialCap(goal, item, completedTrials, index, setIndex);
    // At the cap there is no empty "next trial" slot to step onto.
    const nextBlockedByCap = completedAtCap && index >= completedTrials.length;
    // Set while no new trial may start — disables + explains every start control.
    const capMessage = creationBlocked ? trialCapMessage(maxTrials) : null;
    const { color, background } = renderCardColor(renderColorValue(goal, current));
    const checkPrevNextCase = checkPrevNext(goal);
    const { open } = useModal();
    const disableActions = sessionById?.status !== sessionStatusEnums.IN_PROGRESS;
    const loader = FindLoad(item?.id);
    const deleteTrialSuccess = FindSuccessItem(DELETE_TRIAL_LOG_ACTION_TYPE);
    const disablePrevNext = loader && loader?.find((i) => i?.params?.id === item?.id);
    const [timerInfo, setTimerInfo] = useState({});
    const [liveElapsed, setLiveElapsed] = useState(0);
    const liveIntervalRef = useRef(null);
    // Clamp negative per-trial diffs to 0 — see RateLoggedInfo for rationale.
    const loggedTime = item?.tSummary?.trials?.reduce((acc, trial) => {
        if (!trial?.startTime || !trial?.endTime) return acc;
        const startMoment = moment(trial.startTime);
        const endMoment = moment(trial.endTime);
        const differenceInSeconds = endMoment.diff(startMoment, 'seconds');
        return acc + Math.max(0, differenceInSeconds);
    }, 0);
    const isSupportedType = [
        goalDataTypeEnums.LATENCY,
        goalDataTypeEnums.DURATION,
        goalDataTypeEnums.RATE,
        goalDataTypeEnums.SCORE,
        goalDataTypeEnums.FREQUENCY,
        goalDataTypeEnums.IRT,
    ].includes(goal?.dataType);
    const frequencyLoggedCount = item?.tSummary?.trials?.reduce((acc, curr) => acc + curr.value, 0);
    const hasValue =
        goal?.dataType === goalDataTypeEnums.DURATION
            ? item?.tSummary?.value
            : goal?.dataType === goalDataTypeEnums.RATE
                ? item?.tSummary
                : goal?.dataType === goalDataTypeEnums.FREQUENCY
                    ? // One trial always holds the whole count, so its presence — not the trial
                      // count — is what says "a count was recorded", including a recorded 0.
                      item?.tSummary
                    : current?.value;

    const displayValue =
        goal?.dataType === goalDataTypeEnums.FREQUENCY
            ? null
            : goal?.dataType === goalDataTypeEnums.DURATION
                ? item?.tSummary?.value
                    ? formatTime(item?.tSummary?.value)
                    : '00:00'
                : goal?.dataType === goalDataTypeEnums.RATE
                    ? formatTime(loggedTime)
                    : [goalDataTypeEnums.SCORE].includes(goal?.dataType)
                        ? current?.value
                        : current?.value
                            ? formatTime(current?.value)
                            : '00:00';

    useEffect(() => {
        if (deleteTrialSuccess && deleteTrialSuccess?.data === item?.tSummary?.id) {
            setIndex(item?.tSummary?.trials?.length + 1 || 1);
            dispatch(httpRequestsOnSuccessActions.removeSuccess(DELETE_TRIAL_LOG_ACTION_TYPE));
        }
    }, [deleteTrialSuccess]);

    // Auto-advance the local `index` when ANOTHER tab/clinician completes a
    // trial we were observing together. Without this, Tab B stays on the
    // just-completed slot — `current` latches onto Tab A's COMPLETED trial,
    // the `!current` guard hides the play UI, prev is disabled (index===1),
    // and the next render of the parent saveTSummary leaks Tab A's trial id
    // via the `current?.id` fallback for non-timer types. Fires only when:
    //   1. completedTrials grew (a remote completion landed), AND
    //   2. we were at the empty slot before the new trial arrived
    //      (`index === prev + 1` — the "I'm here, watching this slot" state)
    // Manual prev/next navigation doesn't grow completedTrials, so this
    // effect stays quiet and the user keeps their chosen view.
    const prevCompletedLengthRef = useRef(completedTrials.length);
    useEffect(() => {
        const prev = prevCompletedLengthRef.current;
        prevCompletedLengthRef.current = completedTrials.length;
        if (completedTrials.length > prev && index === prev + 1) {
            setIndex(completedTrials.length + 1);
            dispatch(httpRequestsOnLoadActions.removeLoading(item?.id));

            if (timerInfo?.id === item?.id) {
                setTimerInfo({});
            }
        }
    }, [completedTrials.length]);

    // Resume the active-timer indicator on mount when the API surfaces an
    // IN_PROGRESS trial for this target (refresh / reopen). Without this, the
    // popover-based timer components only set timerInfo when the user opens
    // their popover — so the card-level running indicator would stay dark even
    // though a timer is actually persisted server-side. Also CLEAR timerInfo
    // when no IN_PROGRESS exists — otherwise after Tab A completes the trial,
    // Tab B's `timerInfo.active` stays stuck which disables prev/next and
    // hides the play button (every disabled-condition referencing
    // `timerInfo?.active` would block navigation forever).
    useEffect(() => {
        const inProgress = findInProgressTrial(item);
        if (!inProgress?.startTime) {
            if (timerInfo?.active) setTimerInfo({});
            return;
        }
        if (timerInfo?.active && timerInfo?.startTime === inProgress.startTime) return;
        setTimerInfo({ ...item, active: true, startTime: inProgress.startTime });
    }, [item?.tSummary?.trials]);

    // Live timer: tick every second while timerInfo is active
    useEffect(() => {
        if (timerInfo?.active && timerInfo?.startTime) {
            const calcElapsed = () => {
                const start = moment(timerInfo.startTime);
                return moment().diff(start, 'seconds');
            };
            setLiveElapsed(calcElapsed());
            liveIntervalRef.current = setInterval(() => {
                setLiveElapsed(calcElapsed());
            }, 1000);
        } else {
            setLiveElapsed(0);
            if (liveIntervalRef.current) {
                clearInterval(liveIntervalRef.current);
                liveIntervalRef.current = null;
            }
        }
        return () => {
            if (liveIntervalRef.current) {
                clearInterval(liveIntervalRef.current);
                liveIntervalRef.current = null;
            }
        };
    }, [timerInfo?.active, timerInfo?.startTime]);

    // Returns a Promise resolving to the saved tSummary so timer components can
    // capture the IN_PROGRESS trial id from the start call and reuse it on stop.
    // `option.status` and `option.trialId` let the caller drive lifecycle directly:
    //   - { status: IN_PROGRESS, startTime } on Play           → creates IN_PROGRESS trial
    //   - { trialId, status: COMPLETED, value, endTime } on Stop → updates same trial
    // Index advance + handleClose only fire on COMPLETED so the popover stays open
    // and the trial counter doesn't bump for a half-saved start.
    const saveTSummary = (option, handleClose) => {
        if (sessionById?.status !== sessionStatusEnums.IN_PROGRESS) {
            return Promise.reject(new Error('Session not in progress'));
        }
        const status = option?.status;
        const isCompletion = !status || status === trialStatusEnums.COMPLETED;
        const explicitTrialId = option?.trialId !== undefined;
        // IN_PROGRESS update against an existing trial (e.g. Rate's debounced
        // count save) — skip the global loading + success dispatches so the
        // play/stop spinner doesn't flicker on every +/- click and timer
        // components don't clear their local state mid-trial.
        const isInProgressUpdate = status === trialStatusEnums.IN_PROGRESS && explicitTrialId && !!option.trialId;
        // Only option-button data types (Percent/Rating/Score/Frequency) want to
        // fall back to `current?.id` — the "click an option on the trial I'm
        // viewing" flow. Timer-based types (Rate/Duration/Latency/IRT/Whole/
        // Partial/Momentary) drive trial id via explicit option.trialId from
        // activeTrialId; falling back to `current` here would HIJACK another
        // clinician's just-completed trial because this card's `index` is
        // initialised once from `completedTrials.length + 1` and never
        // re-syncs as other users' trials stream in over socket (so `current`
        // can land on someone else's COMPLETED row). Concretely: User A
        // completes X, socket fires, User B's completedTrials now contains X
        // but User B's index is still 1 → `current = X`. User B's handlePlay
        // sends `saveTSummary({status:IN_PROGRESS, no trialId})`; parent's
        // fallback resolves to X.id; backend routes to updateTrialAtomic
        // instead of createTrialAtomic; the IN_PROGRESS write silently misses
        // (X is COMPLETED, status guard rejects); no socket emit. On Stop,
        // same fallback rewrites X's COMPLETED value with User B's count —
        // User A's data is overwritten.
        const TIMER_TYPES = [
            goalDataTypeEnums.DURATION,
            goalDataTypeEnums.RATE,
            goalDataTypeEnums.LATENCY,
            goalDataTypeEnums.IRT,
            goalDataTypeEnums.WHOLE_INTERVAL,
            goalDataTypeEnums.PARTIAL_INTERVAL,
            goalDataTypeEnums.MOMENTARY_TIME_SAMPLING,
        ];
        const resolvedTrialId = explicitTrialId
            ? option.trialId
            : TIMER_TYPES.includes(goal?.dataType)
                ? null
                : current?.id || null;
        if (!resolvedTrialId && creationBlocked) {
            // Safety net only — start controls are already disabled at the cap.
            // Resolve (not reject): Stop handlers fire-and-forget this promise.
            toast(<NotError type={'error'} errMessage={capMessage} />);
            return Promise.resolve(null);
        }
        const params = {
            goalId: goal?.id,
            targetId: item?.id,
            sessionId: sessionById?.id,
            trialId: resolvedTrialId,
            label: option?.label,
            value: option?.value,
        };
        if (status) {
            params.status = status;
        }
        // Explicit undefined check so a Resume's `isPaused: false` doesn't get
        // dropped — the backend uses the same check to clear the trial flag.
        if (option?.isPaused !== undefined) {
            params.isPaused = option.isPaused;
        }
        // Optimistic-concurrency: echo back the trial's version as the caller
        // last saw it. Backend rejects the write with a silent no-op if another
        // user updated the same trial since this client's last render, giving
        // first-writer-wins / second-silently-ignored semantics. Look up from
        // item.tSummary.trials by id so the version is consistent whether the
        // caller passed an explicit option.trialId (timer flows) or the
        // implicit current?.id (option-button flows).
        //
        // Skip the check for IN_PROGRESS updates: those carry no aggregate-
        // affecting value (numTrials/summary.value only move on the COMPLETED
        // transition) and are issued rapid-fire by the SAME user from local
        // Pause/Resume/checkpoint flows. The render `item` lags the in-flight
        // save by one network round-trip, so a second click before the first
        // response lands would echo the pre-save version and be silently
        // dropped by the backend — start→pause→start→pause would leave the DB
        // stuck on the first pause's state. Last-writer-wins is the correct
        // model for IN_PROGRESS transitions; concurrency only matters when the
        // trial commits to COMPLETED.
        if (resolvedTrialId && !isInProgressUpdate) {
            const trialBeingUpdated = item?.tSummary?.trials?.find((t) => t?.id === resolvedTrialId);
            if (trialBeingUpdated && trialBeingUpdated.version !== undefined) {
                params.expectedVersion = trialBeingUpdated.version;
            }
        }
        if (
            goal?.dataType === goalDataTypeEnums.DURATION ||
            goal?.dataType === goalDataTypeEnums.RATE ||
            goal?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
            goal?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
            goal?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING ||
            goal?.dataType === goalDataTypeEnums.IRT ||
            goal?.dataType === goalDataTypeEnums.LATENCY
        ) {
            params['startTime'] = option?.startTime;
            params['endTime'] = option?.endTime;
        }

        if (!isInProgressUpdate) {
            dispatch(httpRequestsOnLoadActions.appendLoading(item?.id, params));
            dispatch(httpRequestsOnSuccessActions.removeSuccess(item?.id));
        }
        return authService
            .saveSessionTargetOptionService(params)
            .then((res) => {
                dispatch({ type: ACTION_TYPE_SUCCESS, payload: res?.data });
                if (isCompletion) {
                    setIndex(index + 1);
                    handleClose && handleClose();
                }
                if (!isInProgressUpdate) {
                    dispatch(httpRequestsOnLoadActions.removeLoading(item?.id));
                }
                // Suppress the success ping for IN_PROGRESS so timer components
                // don't reset their local state (count, timer, activeTrialId) the
                // moment Play's persistence resolves — that's not the trial ending.
                if (isCompletion) {
                    dispatch(httpRequestsOnSuccessActions.appendSuccess(item?.id));
                }
                return res?.data;
            })
            .catch((err) => {
                toast(<NotError type={'error'} errMessage={err?.data?.message || 'Something went wrong'} />);
                if (!isInProgressUpdate) {
                    dispatch(httpRequestsOnLoadActions.removeLoading(item?.id));
                    dispatch(httpRequestsOnSuccessActions.removeSuccess(item?.id));
                }
                throw err;
            });
    };

    const openHistory = () => {
        if (goal?.dataType === goalDataTypeEnums.RATE) {
            open(<RateHistoryLog goal={item} />);
        } else if (goal?.dataType === goalDataTypeEnums.DURATION || goal?.dataType === goalDataTypeEnums.IRT) {
            open(<DurationHistoryLog goal={item} />);
        } else {
            open(<TrialHistoryLog goal={item} />);
        }
    };

    return (
        <div className="flex-align-start">
            <div className="flex-all-center column">
                <div
                    className="flex-align-center full-width"
                    style={{ gap: 8, position: 'relative', justifyContent: 'center', height: 11 }}
                >
                    {checkPrevNextCase && (
                        <div className="task-card-complete-number" style={{ background }}>
                            <p style={{ color }}>{`${index}/${goal?.minTrials}`}</p>
                        </div>
                    )}

                    {(isSupportedType && hasValue || timerInfo?.active) && (
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            {timerInfo?.active ? (
                                <div className="task-card-timer-box">
                                    <p>
                                        {goal?.dataType === goalDataTypeEnums.DURATION
                                            ? formatTime((item?.tSummary?.value || 0) + liveElapsed)
                                            : formatTime(liveElapsed)}
                                    </p>
                                </div>
                            ) : (
                                displayValue && (
                                    <div className="task-card-timer-box">
                                        <p>{displayValue}</p>
                                    </div>
                                )
                            )}

                            {goal?.dataType === goalDataTypeEnums.RATE && (
                                <div className="task-card-complete-number" style={{ background }}>
                                    <p style={{ color }}>{item?.tSummary?.value}</p>
                                </div>
                            )}

                            {goal?.dataType === goalDataTypeEnums.FREQUENCY && frequencyLoggedCount != null && (
                                <div className="task-card-complete-number" style={{ background }}>
                                    <p style={{ color }}>{frequencyLoggedCount}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {!!item?.tSummary?.trials?.length && (
                        <button className="session-grid-history-btn-box" onClick={openHistory}>
                            <img src={Images.historyTab} alt="resetTimmer" />
                        </button>
                    )}
                </div>

                <div className="task-card-wrapper"
                     style={{ background: timerInfo?.id === item?.id ? '#FDF4D9' : '#F5F9FE' }}>
                    {checkPrevNextCase ? (
                        <button
                            disabled={index === 1 || item?.id === loader?.params?.targetId || disablePrevNext}
                            className="prev-next-btn"
                            onClick={() => setIndex(index - 1)}
                            style={{
                                transform: 'rotate(90deg)',
                                opacity: index === 1 || item?.id === loader?.params?.targetId || disablePrevNext ? 0.5 : 1,
                            }}
                        >
                            <img src={Images.chevronDown} alt="arrow" />
                        </button>
                    ) : (
                        <div />
                    )}

                    {goal?.dataType === goalDataTypeEnums.PERCENT && (
                        <ManageActions
                            btnTitle={item?.name}
                            renderBody={(handleClose) => (
                                <Percent
                                    item={item}
                                    loader={loader}
                                    goal={goal}
                                    current={current}
                                    saveTSummary={(option) => saveTSummary(option, handleClose)}
                                    disableActions={disableActions} capMessage={capMessage}
                                />
                            )}
                        />
                    )}

                    {goal?.dataType === goalDataTypeEnums.RATING && (
                        <ManageActions
                            btnTitle={item?.name}
                            renderBody={(handleClose) => (
                                <Rating
                                    item={item}
                                    loader={loader}
                                    goal={goal}
                                    current={current}
                                    saveTSummary={(option) => saveTSummary(option, handleClose)}
                                    disableActions={disableActions} capMessage={capMessage}
                                />
                            )}
                        />
                    )}

                    {goal?.dataType === goalDataTypeEnums.LATENCY && (
                        <ManageActions
                            btnTitle={item?.name}
                            renderBody={(handleClose) => (
                                <Latency
                                    item={item}
                                    loader={loader}
                                    goal={goal}
                                    index={index}
                                    current={current}
                                    grid={true}
                                    saveTSummary={(option) => saveTSummary(option, handleClose)}
                                    disableActions={disableActions} capMessage={capMessage}
                                />
                            )}
                        />
                    )}

                    {goal?.dataType === goalDataTypeEnums.DURATION && (
                        <GridDuration
                            index={index}
                            btnTitle={item?.name}
                            item={item}
                            loader={loader}
                            disableActions={disableActions} capMessage={capMessage}
                            setTimerInfo={setTimerInfo}
                            saveTSummary={(option, handleClose) => {
                                const result = saveTSummary(option, handleClose);
                                // Only clear the active-timer indicator on COMPLETED;
                                // Play's IN_PROGRESS save must keep the indicator lit.
                                if (!option?.status || option.status === trialStatusEnums.COMPLETED) {
                                    timerInfo && setTimerInfo({});
                                }
                                return result;
                            }}
                        />
                    )}

                    {goal?.dataType === goalDataTypeEnums.FREQUENCY && (
                        <ManageActions
                            btnTitle={item?.name}
                            renderBody={(handleClose) => (
                                <Frequency
                                    item={item}
                                    loader={loader}
                                    current={current}
                                    grid={true}
                                    saveTSummary={(option) => saveTSummary(option, handleClose)}
                                    disableActions={disableActions} capMessage={capMessage}
                                />
                            )}
                        />
                    )}

                    {goal?.dataType === goalDataTypeEnums.SCORE && (
                        <ManageActions
                            btnTitle={item?.name}
                            renderBody={(handleClose) => (
                                <Scale
                                    item={item}
                                    loader={loader}
                                    current={current}
                                    saveTSummary={(option) => saveTSummary(option, handleClose)}
                                    disableActions={disableActions} capMessage={capMessage}
                                />
                            )}
                        />
                    )}

                    {(goal?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
                        goal?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
                        goal?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING) && (
                        <div>
                            <GridWholeInterval
                                index={index}
                                btnTitle={item?.name}
                                item={item}
                                loader={loader}
                                goal={goal}
                                current={current}
                                disableActions={disableActions} capMessage={capMessage}
                                setTimerInfo={setTimerInfo}
                                saveTSummary={(option, handleClose) => {
                                    const result = saveTSummary(option, handleClose);
                                    // Only clear the active-timer indicator on COMPLETED;
                                    // Play's IN_PROGRESS save must keep the indicator lit.
                                    if (!option?.status || option.status === trialStatusEnums.COMPLETED) {
                                        timerInfo && setTimerInfo({});
                                    }
                                    return result;
                                }}
                            />
                        </div>
                    )}

                    {goal?.dataType === goalDataTypeEnums.RATE && (
                        <GridRate
                            // handleDurationHistory={(item) => open(<RateHistoryLog goal={item} />)}
                            index={index}
                            btnTitle={item?.name}
                            item={item}
                            loader={loader}
                            disableActions={disableActions} capMessage={capMessage}
                            setTimerInfo={setTimerInfo}
                            saveTSummary={(option, handleClose) => {
                                const result = saveTSummary(option, handleClose);
                                // Only clear the active-timer indicator on COMPLETED;
                                // Play's IN_PROGRESS save must keep the indicator lit.
                                if (!option?.status || option.status === trialStatusEnums.COMPLETED) {
                                    timerInfo && setTimerInfo({});
                                }
                                return result;
                            }}
                        />
                    )}

                    {goal?.dataType === goalDataTypeEnums.IRT && (
                        <GridIrt
                            index={index}
                            btnTitle={item?.name}
                            item={item}
                            loader={loader}
                            goal={goal}
                            current={current}
                            disableActions={disableActions} capMessage={capMessage}
                            setTimerInfo={setTimerInfo}
                            saveTSummary={(option, handleClose) => {
                                const result = saveTSummary(option, handleClose);
                                // Only clear the active-timer indicator on COMPLETED;
                                // Play's IN_PROGRESS save must keep the indicator lit.
                                if (!option?.status || option.status === trialStatusEnums.COMPLETED) {
                                    timerInfo && setTimerInfo({});
                                }
                                return result;
                            }}
                        />
                    )}

                    {checkPrevNextCase ? (
                        <SimpleTooltip title={nextBlockedByCap ? trialCapMessage(maxTrials) : ''} placement="top" arrow>
                        <span style={{ display: 'inline-flex' }}>
                        <button
                            disabled={item?.id === loader?.params?.targetId || !current?.id || disablePrevNext || nextBlockedByCap}
                            onClick={() => setIndex(index + 1)}
                            className="prev-next-btn"
                            style={{
                                opacity: !current?.id || item?.id === loader?.params?.targetId || disablePrevNext || nextBlockedByCap ? 0.5 : 1,
                                transform: 'rotate(-90deg)',
                                filter: 'brightness(0) saturate(100%) invert(25%) sepia(100%) saturate(3259%) hue-rotate(209deg) brightness(95%) contrast(91%)',
                            }}
                        >
                            <img src={Images.chevronDown} alt="arrow" />
                        </button>
                        </span>
                        </SimpleTooltip>
                    ) : (
                        <div />
                    )}
                </div>

                {item?.isMaintenance && (
                    <div className="meintenance-card-box">
                        <StatusCard
                            status={renderTargetStatuses?.find((j) => targetStatusEnums.MAINTENANCE === j?.value)?.label}
                            custom={renderTargetStatuses?.find((j) => targetStatusEnums.MAINTENANCE === j?.value)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
